import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, apiClient } from '../services/api';
import { secureStorage } from '../services/SecureStorageService';
import { localStorageService } from '../services/LocalStorageService';
import { appStore } from '../stores/AppStore';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithGoogle: (googleCredential: string, deviceName?: string) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
  clearError: () => void;
  handleTokenExpiration: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isAuthenticated = !!user;

  // Generate unique device ID for web (non-sensitive, can persist in localStorage)
  const getDeviceId = (): string => {
    let deviceId = secureStorage.getDeviceId();
    if (!deviceId) {
      // Generate a more unique device ID
      const timestamp = Date.now();
      const randomPart = Math.random().toString(36).substr(2, 12);
      deviceId = `web_${randomPart}_${timestamp}`;
      secureStorage.setDeviceId(deviceId);
    }
    return deviceId;
  };

  const loginWithGoogle = async (googleCredential: string, deviceName?: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const deviceId = getDeviceId();
      
      // Try to login first, if user doesn't exist, register them
      try {
        const response = await apiClient.login({
          googleToken: googleCredential,
          deviceId,
          deviceName: deviceName || 'Web Browser'
        });
        setUser(response.user);
        // Store minimal user data in localStorage with 7-day expiration
        // Force reset expiration on initial login (session persists for 7 days)
        secureStorage.setUserData(response.user, true);
        // Start automatic token refresh
        apiClient.startTokenRefresh();
      } catch (loginError) {
        // If login fails, try to register the user
        const response = await apiClient.register({
          googleToken: googleCredential,
          deviceId,
          deviceName: deviceName || 'Web Browser'
        });
        setUser(response.user);
        // Store minimal user data in localStorage with 7-day expiration
        // Force reset expiration on initial registration (session persists for 7 days)
        secureStorage.setUserData(response.user, true);
        // Start automatic token refresh
        apiClient.startTokenRefresh();
      }
    } catch (err: any) {
      setError(err.message || 'Google authentication failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Stop automatic token refresh
      apiClient.stopTokenRefresh();
      
      // Get current device ID before clearing tokens
      const deviceId = secureStorage.getDeviceId();
      
      // Delete device from server if device ID exists
      if (deviceId) {
        try {
          await apiClient.deleteDevice(deviceId);
          console.log('Device deleted from server:', deviceId);
        } catch (err) {
          console.error('Failed to delete device from server:', err);
          // Continue with logout even if device deletion fails
        }
      }
      
      // Clear all messages from IndexedDB
      try {
        await localStorageService.clearAllMessages();
        console.log('All messages cleared from IndexedDB');
      } catch (err) {
        console.error('Failed to clear messages from IndexedDB:', err);
        // Continue with logout even if message clearing fails
      }
      
      // Clear all data from AppStore
      appStore.clearAllData();
      
      // Call logout API
      await apiClient.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      // Clear tokens (localStorage)
      secureStorage.clearTokens();
      // Clear device ID - user is logging out, device should be removed
      secureStorage.clearDeviceId();
      setIsLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const handleTokenExpiration = () => {
    // Stop automatic token refresh
    apiClient.stopTokenRefresh();
    setUser(null);
    // Clear tokens from localStorage
    secureStorage.clearTokens();
    // Note: We keep device ID so the same device can be reused
    setError('Your session has expired. Please sign in again to continue.');
  };

  // Set up token expiration handler
  useEffect(() => {
    apiClient.setTokenExpirationHandler(handleTokenExpiration);
  }, []);

  // Check if user is already authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const token = secureStorage.getAccessToken();
      const userData = secureStorage.getUserData();
      
      if (token && userData) {
        try {
          // Restore user from localStorage first (for immediate UI update)
          setUser(userData);
          
          // Validate token with backend
          const isValid = await apiClient.validateToken();
          if (!isValid) {
            // Token is invalid, clear session
            setUser(null);
            secureStorage.clearTokens();
            console.log('Token validation failed, session cleared');
          } else {
            console.log('Session restored and validated');
            // Start automatic token refresh for existing session
            apiClient.startTokenRefresh();
          }
        } catch (err) {
          console.error('Failed to validate token:', err);
          // Clear corrupted data
          setUser(null);
          secureStorage.clearTokens();
        }
      } else if (token) {
        // Token exists but no user data - validate and fetch user
        try {
          const isValid = await apiClient.validateToken();
          if (isValid) {
            // Token is valid, try to get user from devices endpoint
            try {
              const devices = await apiClient.getDevices();
              // User data will be set when they interact with the app
              console.log('Session restored, user data will be fetched on interaction');
              apiClient.startTokenRefresh();
            } catch (err) {
              console.error('Failed to fetch user data:', err);
              secureStorage.clearTokens();
            }
          } else {
            secureStorage.clearTokens();
          }
        } catch (err) {
          console.error('Failed to validate token:', err);
          secureStorage.clearTokens();
        }
      }
      
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    loginWithGoogle,
    logout,
    error,
    clearError,
    handleTokenExpiration
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
